// Google Identity Services adapter.
//
// Keeps the imperative third-party lifecycle - injecting a <script>, waiting for
// it, registering a callback, rendering their button - out of React components,
// which otherwise end up owning DOM setup and teardown they have no business with.

const GSI_SRC = "https://accounts.google.com/gsi/client";

/**
 * Load Google Identity Services and render its sign-in button.
 *
 * @param {Object} options
 * @param {string} options.clientId
 * @param {string} options.buttonElementId id of the element to render into
 * @param {(credential: string) => void} options.onCredential receives the JWT
 * @param {Object} [options.buttonOptions] passed through to renderButton
 * @returns {() => void} cleanup - call it on unmount
 */
export const loadGoogleIdentity = ({
  clientId,
  buttonElementId,
  onCredential,
  buttonOptions = {
    theme: "outline",
    size: "large",
    text: "signin_with",
    shape: "rectangular",
    width: 260,
  },
}) => {
  const initialize = () => {
    if (!window.google || !window.google.accounts) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => onCredential(response.credential),
    });

    const target = document.getElementById(buttonElementId);
    if (target) {
      window.google.accounts.id.renderButton(target, buttonOptions);
    }
  };

  const script = document.createElement("script");
  script.src = GSI_SRC;
  script.async = true;
  script.defer = true;
  script.onload = initialize;
  document.body.appendChild(script);

  return () => {
    // The node may already be gone if another unmount removed it first.
    try {
      document.body.removeChild(script);
    } catch {}
  };
};
